import * as React from 'react';
import { ReactNode } from 'react';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { ApiResponse, ExternalIdDTO, ReferencedOrganisationDTO, SolrDocument, SuggestApi } from '../../../../api/core';
import { Suggester } from './Suggester';
import { ExternalId } from '../../../detail/components/ExternalId';
import { renderSelectedTemplate, topOrgaIcons } from './utils';

interface Props {
  value: SelectedOrganisation[];
  onChange?: (values: SelectedOrganisation[]) => void;
  multiple: boolean;
  disabled?: boolean;
  required?: boolean;
  validation?: ValidationResult;
  inputId?: string;
  autoFocus?: boolean;
  border?: 'border-noround' | 'border-noround-right';
  tooltip?: string;
  tooltipOptions?: any;
}

export interface SelectedOrganisation {
  id: number;
  name: string;
  topOrgas: string[];
  aka: string[];
  externalIds: ExternalIdDTO[];
}

interface SolrOrganisation extends SolrDocument {
  id: string;
  f_top_orga: string;
  organisation_name: string;
  other_name: string[];
  gnd_id: string[];
  orcid_id: string[];
}

// TODO remove "(referenced as any) when after ReferencedOrganisation getting props topOrgas
export const referencedOrganisationToSelectedOrganisation = (referenced: ReferencedOrganisationDTO): SelectedOrganisation => {
  return {
    id: referenced.id,
    name: referenced.name,
    topOrgas: referenced.topOrgas,
    aka: [],
    externalIds: [],
  };
};

export const selectedOrganisationToReferencedOrganisation = (selected: SelectedOrganisation): ReferencedOrganisationDTO => {
  return {
    id: selected.id,
    name: selected.name,
    topOrgas: selected.topOrgas,
  } as ReferencedOrganisationDTO;
};

export function OrganisationSuggester(props: Props): ReactNode {
  const solrToSelectedOrganisation = (solr: SolrOrganisation): SelectedOrganisation => {
    return {
      id: +solr.id,
      name: solr.organisation_name,
      aka: solr.other_name || [],
      topOrgas: [solr.f_top_orga],
      externalIds: [
        ...(solr.gnd_id || []).map((externalId: string): ExternalIdDTO => ({ externalId, type: 'gnd' })),
        ...(solr.orcid_id || []).map(
          (externalId: string): ExternalIdDTO => ({
            externalId,
            type: 'orcid',
          })
        ),
      ],
    };
  };

  const request = (params: { q: string }): Promise<ApiResponse<SolrDocument[]>> =>
    new SuggestApi().organisationSuggestRaw(params);

  const selectedItemTemplate = (item: SelectedOrganisation): ReactNode => {
    const icons: { icon: string; title: string }[] = topOrgaIcons(item.topOrgas || []);
    return renderSelectedTemplate(`${item.name} (${item.id})`, icons);
  };

  const itemTemplate = (item: SelectedOrganisation): ReactNode => {
    const icons: { icon: string; title: string }[] = topOrgaIcons(item.topOrgas || []);
    return <div className={'max-w-30rem flex'}>
      <div className={'w-1 flex flex-column gap-1'}>
        {icons.length > 0 && icons.map((icon: { icon: string; title: string }) =>
          <img src={icon.icon} alt={`${icon.title} Logo Icon`} className={'max-w-2rem'}/>,
        )}
      </div>
      <div className={'w-11 flex flex-column gap-1'}>
        <div className={'flex flex-column white-space-normal'}>
          <span><strong>{item.name}</strong> ({item.id})</span>
          {item.aka && item.aka.length > 0 && <small>{item.aka.join(', ')}</small>}
        </div>
        {item.topOrgas && <div className={'flex flex-column white-space-normal'}>
          {item.topOrgas.join('; ')}
        </div>}
        {item.externalIds && item.externalIds.length > 0 &&
          <div className={'flex gap-1'}>
            {(item.externalIds || []).map((externalId: ExternalIdDTO, idx: number) =>
              <div key={idx}><ExternalId id={{...externalId}} entity={'orga'}/></div>,
            )}
          </div>
        }
      </div>
    </div>;
  };

  return (
    <Suggester<SelectedOrganisation, SelectedOrganisation>
      itemTemplate={itemTemplate}
      autoFocus={props.autoFocus}
      selectedItemTemplate={selectedItemTemplate}
      load={request}
      map={solrToSelectedOrganisation}
      inputId={props.inputId}
      value={props.value || []}
      disabled={props.disabled}
      validation={props.validation}
      required={props.required}
      onChange={props.onChange}
      border={props.border}
      tooltip={props.tooltip}
      tooltipOptions={props.tooltipOptions}
      multiple={props.multiple}/>
  );
}
